import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Button,
    Form,
    Input,
    InputNumber,
    Select,
    Col,
    Row,
    Flex,
} from 'antd';
import { CKEditor } from "ckeditor4-react";
import axios from 'axios';
import * as adminactions from "store/actions/AuthActions";
import { notify } from "components";
import { compactToolbar } from "utilities/config";

export const UNIT_OPTIONS = [
    { value: "DAYS", label: "DAYS" },
    { value: "HOURS", label: "HOURS" },
    { value: "FIXED", label: "FIXED" },
    { value: "UNITS", label: "UNITS" },
];

const ProductForm = ({ instance, form, taxRates = [], reload }) => {

    const [loading, setLoading] = useState(false);
    const [unit, setUnit] = useState("UNITS");

    // CKEditor is rebuilt whenever it gets a new `config` object, so the config has to
    // keep a stable identity across renders. Content is pushed through the editor
    // instance instead of `initData`, which is only read once on mount.
    const editorRef = useRef(null);
    const descriptionRef = useRef("");

    // Fix double-slash issue: REACT_APP_BASE_URL ends with '/'
    const baseUrl = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
    const editorUrl = `${baseUrl}/staticfiles/ckeditor/4.19.0/full-all/ckeditor.js`;
    const editorConfig = useMemo(() => ({
        extraPlugins: ["tableresize", "uploadimage", "createpdf"],
        removePlugins: [
            "exportpdf",
            "forms",
            "smiley",
            "language",
            "iframe",
            "about",
            "save",
            "preview",
            "print",
        ],
        filebrowserImageUploadUrl: `${baseUrl}/template/upload_template_image/`,
        fileTools_requestHeaders: axios.defaults.headers.common,
        uploadUrl: `${baseUrl}/template/upload_template_image/`,
        contentsCss: [`${baseUrl}/staticfiles/ckeditor/4.19.0/full-all/document-style.css`, "/ckeditor-content.css"],
        bodyClass: "document-editor",
        height: "5cm",
        allowedContent: true,
        protectedSource: [/{%[\s\S]*?%}+/g, /{{[\s\S]*?}}+/g],
      // Phones get a short toolbar; desktop keeps the full grouped one.
      ...compactToolbar(),
    }), [baseUrl]);

    const setEditorContent = (html) => {
        descriptionRef.current = html || "";
        if (editorRef.current) {
            editorRef.current.setData(descriptionRef.current);
        }
    };

    // Same formula the invoice/quote line forms use, so a product's line total
    // matches what it produces once it is dropped onto an invoice.
    const recalculateLineTotal = () => {
        const quantity = Number(form.getFieldValue("quantity")) || 0;
        const price = Number(form.getFieldValue("price")) || 0;
        const rateOf = (taxRateId) => {
            const taxRate = taxRates.find((item) => item.id === taxRateId);
            return taxRate ? taxRate.rate / 100 : 0;
        };
        const taxMultiplier =
            rateOf(form.getFieldValue("tax_rate1")) +
            rateOf(form.getFieldValue("tax_rate2")) +
            rateOf(form.getFieldValue("tax_rate3"));

        const base = quantity * price;
        form.setFieldValue("line_total", Number((base + base * taxMultiplier).toFixed(2)));
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const payload = {
                ...values,
                price: values.price ?? 0,
                quantity: values.unit === "FIXED" ? 1 : (values.quantity ?? 1),
                is_fixed_rate: values.unit === "FIXED",
                line_total: Number(values.line_total) || 0,
                tax_rate1: values.tax_rate1 || null,
                tax_rate2: values.tax_rate2 || null,
                tax_rate3: values.tax_rate3 || null,
            };

            const response = instance
                ? await adminactions.updateProducts(payload, instance.id)
                : await adminactions.addProducts(payload);

            if (response?.success) {
                notify("success", `Product ${instance ? "updated" : "added"} successfully`);
                reload(response.data);
            } else {
                notify("error", `Failed to ${instance ? "update" : "add"} product`, response?.error);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (instance) {
            // Older products only carry plain `notes`; show them as the description.
            const instanceDescription = instance.description || instance.notes || "";
            setEditorContent(instanceDescription);
            setUnit(instance.unit || "UNITS");
            form.setFieldsValue({
                ...instance,
                description: instanceDescription,
                quantity: instance.quantity ?? 1,
            });
        } else {
            setEditorContent("");
            setUnit("UNITS");
            form.setFieldsValue({
                title: undefined,
                description: "",
                hsn: undefined,
                unit: "UNITS",
                price: undefined,
                quantity: 1,
                tax_rate1: undefined,
                tax_rate2: undefined,
                tax_rate3: undefined,
                line_total: undefined,
            });
        }
    }, [instance]);

    const taxRateOptions = taxRates?.map((item) => ({
        value: item.id,
        label: `${item.title + " " + item.rate} %`,
    }));

    return (
        <Form
            form={form}
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 18 }}
            onFinish={onFinish}
            initialValues={{ quantity: 1, unit: "UNITS" }}
        >
            <Row style={{ margin: "15px" }}>
                <Col span={24}>
                    <div className="site-card-border-less-wrapper">
                        <div>
                            <Form.Item
                                label="Title"
                                name='title'
                                rules={[{ required: true, message: "Please input title!" }]}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                label="Description"
                                name="description"
                                getValueFromEvent={(event) => event.editor.getData()}
                            >
                                <CKEditor
                                    initData=""
                                    config={editorConfig}
                                    editorUrl={editorUrl}
                                    onInstanceReady={({ editor }) => {
                                        editorRef.current = editor;
                                        editor.setData(descriptionRef.current);
                                    }}
                                    onDestroy={() => {
                                        editorRef.current = null;
                                    }}
                                />
                            </Form.Item>
                            <Form.Item label="HSN/SAC" name='hsn'>
                                <Input />
                            </Form.Item>
                            <Form.Item
                                label="Unit"
                                name='unit'
                                rules={[{ required: true, message: "Please select a unit!" }]}
                            >
                                <Select
                                    options={UNIT_OPTIONS}
                                    onChange={(value) => {
                                        setUnit(value);
                                        if (value === "FIXED") {
                                            form.setFieldValue("quantity", 1);
                                        }
                                        recalculateLineTotal();
                                    }}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Price/unit"
                                name='price'
                                rules={[{ required: true, message: "Please input price!" }]}
                            >
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    precision={2}
                                    style={{ width: "100%" }}
                                    onChange={recalculateLineTotal}
                                />
                            </Form.Item>
                            <Form.Item label="Quantity" name='quantity'>
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    precision={2}
                                    style={{ width: "100%" }}
                                    disabled={unit === "FIXED"}
                                    onChange={recalculateLineTotal}
                                />
                            </Form.Item>
                            {["tax_rate1", "tax_rate2", "tax_rate3"].map((name, index) => (
                                <Form.Item key={name} label={`Tax Rate ${index + 1}`} name={name}>
                                    <Select
                                        showSearch
                                        placeholder="Select Tax Rate"
                                        allowClear
                                        onChange={recalculateLineTotal}
                                        filterOption={(input, option) =>
                                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={taxRateOptions}
                                    />
                                </Form.Item>
                            ))}
                            <Form.Item label="Line Total" name='line_total'>
                                <Input disabled />
                            </Form.Item>
                        </div>
                        <Flex justify='flex-end'>
                            <Button
                                block
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                style={{ maxWidth: 100 }}
                            >
                                Save
                            </Button>
                        </Flex>
                    </div>
                </Col>
            </Row>
        </Form>
    );
};
export default ProductForm;
